import type React from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { trackEvent } from "@/features/analytics";
import { useAuth } from "@/features/auth";
import { createProject, deleteProject, loadProjects, updateProject } from "./repository";
import type { ProjectData, ProjectListItem } from "./types";

const useProjectListValue = () => {
  const { user } = useAuth();
  const currentProjectId = useParams<{ projectId: string }>().projectId;
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const lastOpenedProjectId = useRef<string | undefined>(undefined);

  useEffect(() => {
    loadProjects().then(setProjects);
  }, []);

  useEffect(() => {
    if (currentProjectId && currentProjectId !== lastOpenedProjectId.current) {
      trackEvent("project_open", { project_id: currentProjectId, source: "route" });
      lastOpenedProjectId.current = currentProjectId;
    }
    if (!currentProjectId) {
      lastOpenedProjectId.current = undefined;
    }
  }, [currentProjectId]);

  const gotoProject = useCallback(
    (projectId: string | null) => {
      navigate(`/${projectId || ""}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    async (projectId: string) => {
      trackEvent("project_delete", { project_id: projectId });
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      await deleteProject(user.uid, projectId);
      if (currentProjectId === projectId) {
        navigate(`/`);
      }
    },
    [user.uid, currentProjectId, navigate],
  );

  const renameProject = useCallback(
    async (projectId: string, newTitle: string) => {
      trackEvent("project_rename", { project_id: projectId });
      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId ? { ...p, title: newTitle, updatedAt: Date.now() } : p,
        ),
      );
      await updateProject(user.uid, projectId, { title: newTitle });
    },
    [user.uid],
  );

  const newProject = useCallback(
    async (data: ProjectData) => {
      const project = await createProject(user.uid, data);
      trackEvent("project_create", { project_id: project.id });
      setProjects((prev) => [project, ...prev]);
      gotoProject(project.id);
      return project;
    },
    [user.uid, gotoProject],
  );

  const updateProjectListItem = useCallback(async (id: string, data: Partial<ProjectListItem>) => {
    setProjects((prev) => prev.map((p) => (p.id === id ? { ...p, ...data } : p)));
  }, []);

  return {
    currentProjectId,
    projects,
    updateProjectListItem,
    deleteProject: handleDelete,
    renameProject,
    newProject,
    gotoProject,
  };
};

type ProjectListContextValue = ReturnType<typeof useProjectListValue>;

const ProjectListContext = createContext<ProjectListContextValue | null>(null);

export const useProjectList = () => {
  const ctx = useContext(ProjectListContext);
  if (!ctx) throw new Error("useProjectList must be used within ProjectListProvider");
  return ctx;
};

interface ProjectListProviderProps {
  children: React.ReactNode;
}

export const ProjectListProvider: React.FC<ProjectListProviderProps> = ({ children }) => {
  const value = useProjectListValue();
  return <ProjectListContext.Provider value={value}>{children}</ProjectListContext.Provider>;
};
